import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Spinner } from '@/components/atoms/Spinner';
import { NotFound } from '@/components/molecules/NotFound';
import { ToastInfo, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { type ApiUser, useGetApiUsers } from '@/domain/apiUsers';
import {
    useCreateManyCages,
    useGetCageTypes,
    useGetFishTypes,
    useGetLiceTypes,
    useGetLocationTypes,
    useUpdateCage,
} from '@/domain/cages';
import { useGetClientWithLocations, useUpdateClient } from '@/domain/client';
import {
    type LocationFormInput,
    type LocationUpdateInput,
    useCreateLocation,
    useMarkCageHistorical,
    useUpdateLocation,
} from '@/domain/locations';
import { isNotFoundError } from '@/domain/shared/errors';

import Client from './Client';

export interface ClientCage {
    id: number;
    name: string;
}
export interface ClientLocation {
    id: number;
    name: string;
    cages: ClientCage[];
}

function ClientContainer(props: { clientId: number }) {
    const { clientId } = props;
    const { t } = useTranslation();
    // API users created on this page, shown before the list is next fetched.
    const [createdApiUsers, setCreatedApiUsers] = useState<ApiUser[]>([]);

    const clientWithLocationsRequest = useGetClientWithLocations(clientId);
    const cageTypes = useGetCageTypes();
    const fishTypes = useGetFishTypes();
    const liceTypes = useGetLiceTypes();
    const locationTypes = useGetLocationTypes();
    const typeLists = [cageTypes, fishTypes, liceTypes, locationTypes];
    // Every dialog of the location section picks from these lists, so the section stands
    // aside while one of them failed.
    const typesLoadError = typeLists.some((query) => query.error)
        ? retryFailedQueries(...typeLists)
        : null;

    const { mutateAsync: createLocation } = useCreateLocation(clientId, {
        onSettled: () => clientWithLocationsRequest.refetch(),
    });
    const clientCreateLocation = (input: LocationFormInput) => createLocation(input);

    const { mutateAsync: updateLocation } = useUpdateLocation({
        onSettled: () => clientWithLocationsRequest.refetch(),
    });
    const locationUpdateLocation = (input: LocationUpdateInput) => updateLocation(input);

    const { mutateAsync: createManyCagesMutation } = useCreateManyCages({
        onSettled: () => clientWithLocationsRequest.refetch(),
    });
    const createManyCages = (
        locationId: number,
        cageTypeId: number,
        fishTypeId: number,
        names: string[]
    ) => {
        return createManyCagesMutation({
            requestBody: {
                locationId,
                cageTypeId,
                fishTypeId,
                names,
            },
        });
    };

    const { mutateAsync: updateCageMutation } = useUpdateCage({
        onSettled: () => clientWithLocationsRequest.refetch(),
    });
    const updateCage = (
        cageId: number,
        input: { name: string; cageTypeId: number; fishTypeId: number }
    ) => {
        return updateCageMutation({ cageId, requestBody: input });
    };

    const apiUsersRequest = useGetApiUsers(clientId);
    const {
        data: apiUsersQuery,
        isLoading: apiUsersLoading,
        error: apiUsersError,
    } = apiUsersRequest;

    const apiUsers = useMemo(() => {
        const listed = apiUsersQuery ?? [];
        const listedIds = new Set(listed.map((apiUser) => apiUser.id));
        return [...listed, ...createdApiUsers.filter((apiUser) => !listedIds.has(apiUser.id))];
    }, [apiUsersQuery, createdApiUsers]);

    const clientLocations: ClientLocation[] = useMemo(() => {
        return (
            clientWithLocationsRequest.data?.locations.map((location) => ({
                id: location.id,
                name: location.name,
                cages: location.cages.map((cage) => ({
                    id: cage.id,
                    name: cage.name,
                })),
            })) || []
        );
    }, [clientWithLocationsRequest.data?.locations]);

    const handleAddCreatedApiUser = useCallback((apiUser: ApiUser) => {
        setCreatedApiUsers((prevApiUsers) => [...prevApiUsers, apiUser]);
    }, []);

    const { mutateAsync: markHistorical } = useMarkCageHistorical({
        onSettled: () => clientWithLocationsRequest.refetch(),
    });

    // Refetches the client list and the session too once it settles.
    const { mutateAsync: updateClient } = useUpdateClient({
        onSettled: () => clientWithLocationsRequest.refetch(),
    });

    const handleUpdateClient = async (
        clientId: number,
        data: { name?: string; archived?: boolean },
        options?: { reportError?: boolean }
    ) => {
        try {
            await updateClient({ clientId, changes: data });
            // Show success toast
            const messages = [];
            if (data.name !== undefined) {
                messages.push(t('internal.client.nameUpdated'));
            }
            if (data.archived !== undefined) {
                messages.push(
                    data.archived ? t('internal.client.archived') : t('internal.client.unarchived')
                );
            }
            ToastSuccess(
                messages.join(', ') || t('internal.client.updated'),
                `client-update-success-${clientId}`
            );
        } catch (error: unknown) {
            // Archive and restore report the failure in the confirm dialog, so they
            // skip this toast. A rename has no dialog and needs it. The throw keeps
            // either one open so the user can retry.
            if (options?.reportError !== false) {
                ToastInfo({
                    content: apiErrorContent(error, t('internal.client.updateFailed')),
                    status: 'error',
                    icon: 'noicon',
                    options: {
                        toastId: `update-error-${clientId}-${Date.now()}`,
                        position: 'top-right',
                        autoClose: 4000,
                        closeOnClick: true,
                        hideProgressBar: false,
                        pauseOnHover: true,
                    },
                });
            }
            throw error;
        }
    };

    const isLoading = clientWithLocationsRequest.isLoading || apiUsersLoading;
    const isError = clientWithLocationsRequest.error || apiUsersError;
    if (isLoading) {
        return <Spinner size="lg" fullScreen />;
    }

    // The endpoint answers an unknown id with an empty 200, and an id the user may not open
    // with a 403; both are not found rather than a page that waits or fails.
    const clientData = clientWithLocationsRequest.data;
    const clientError = clientWithLocationsRequest.error;
    if (isNotFoundError(clientError) || (!clientError && !clientData)) {
        return <NotFound />;
    }

    // The client's name heads the page once it loads, so the error state stands in as its h1.
    if (isError || !clientData) {
        return (
            <SomethingHappened
                headingLevel="h1"
                {...retryFailedQueries(clientWithLocationsRequest, apiUsersRequest)}
            />
        );
    }

    const client = {
        ...clientData,
        locations: clientData.locations.map((location) => ({
            ...location,
            fishType: location.fishTypes ?? null,
            // The response carries only the relation, not the scalar, and a site without a
            // default species has fishTypes === null (WB-2207).
            fishtypeid: location.fishTypes?.id ?? null,
            liceTypes: location.lice_type,
            licetypeid: location.lice_type.id,
            locationtypeid: location.location_type.id,
            cages: location.cages.map((cage) => ({
                ...cage,

                markHistorical: (historical: boolean) =>
                    markHistorical({ cageId: cage.id, historical }),
            })),
        })),
    };

    return (
        <Client
            cageTypesList={cageTypes.data}
            fishTypesList={fishTypes.data}
            liceTypesList={liceTypes.data ?? []}
            locationTypesList={locationTypes.data ?? []}
            typesLoadError={typesLoadError}
            refetch={clientWithLocationsRequest.refetch}
            client={client}
            apiUsers={apiUsers}
            clientLocations={clientLocations}
            addCreatedApiUser={handleAddCreatedApiUser}
            updateLocation={locationUpdateLocation}
            createNewLocation={clientCreateLocation}
            createManyCages={createManyCages}
            updateCage={updateCage}
            onUpdateClient={handleUpdateClient}
        />
    );
}

export default ClientContainer;
